import React, { useState, useEffect, useContext } from "react";
import {
  Table,
  Card,
  Row,
  Button,
  Space,
  DatePicker,
  Select,
  Typography,
  Spin,
  Flex,
  Tooltip,
  message,
} from "antd";
import {
  DownloadOutlined,
  ReloadOutlined,
  SearchOutlined,
  QuestionCircleOutlined,
  CloudSyncOutlined,
} from "@ant-design/icons";
import { CardTitle, ColumnComponent } from "../../components/ui";
import { useParams, useSearchParams } from "react-router-dom";
import AuthContext from "../../context/AuthContext";
import DataContext from "../../context/DataContext";
import dayjs from "dayjs";
import type { ColumnsType } from "antd/es/table";
import type { TablePaginationConfig } from "antd/es/table";

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// Interface for the summary data
interface CustomerYearSummaryRow {
  customerId: string;
  customerName: string;
  year: number;
  invoices_count: number;
  invoices_total: number;
  creditMemos_count: number;
  creditMemos_total: number;
  payments_count: number;
  payments_total: number;
}

interface CustomerYearSummaryResponse {
  page: number;
  pageSize: number;
  total: number;
  rows: CustomerYearSummaryRow[];
  success: boolean;
}

// Format currency using USD formatter
const USDollar = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

// Column definitions for the table
const getColumns = (): ColumnsType<CustomerYearSummaryRow> => [
  {
    title: "Customer",
    dataIndex: "customerName",
    key: "customerName",
    sorter: true,
    fixed: "left",
    width: 200,
    render: (text: string) => <Text strong>{text || "Unknown Customer"}</Text>,
  },
  {
    title: "Year",
    dataIndex: "year",
    key: "year",
    sorter: true,
    width: 100,
    align: "center",
  },
  {
    title: "Invoices",
    children: [
      {
        title: "Count",
        dataIndex: "invoices_count",
        key: "invoices_count",
        sorter: true,
        width: 100,
        align: "right",
      },
      {
        title: "Total",
        dataIndex: "invoices_total",
        key: "invoices_total",
        sorter: true,
        width: 120,
        align: "right",
        render: (value: number) => USDollar.format(value || 0),
      },
    ],
  },
  {
    title: "Credit Memos",
    children: [
      {
        title: "Count",
        dataIndex: "creditMemos_count",
        key: "creditMemos_count",
        sorter: true,
        width: 100,
        align: "right",
      },
      {
        title: "Total",
        dataIndex: "creditMemos_total",
        key: "creditMemos_total",
        sorter: true,
        width: 120,
        align: "right",
        render: (value: number) => USDollar.format(value || 0),
      },
    ],
  },
  {
    title: "Payments",
    children: [
      {
        title: "Count",
        dataIndex: "payments_count",
        key: "payments_count",
        sorter: true,
        width: 100,
        align: "right",
      },
      {
        title: "Total",
        dataIndex: "payments_total",
        key: "payments_total",
        sorter: true,
        width: 120,
        align: "right",
        render: (value: number) => USDollar.format(value || 0),
      },
    ],
  },
];

export const CustomerYearSummaryPage: React.FC = () => {
  const { idClient } = useParams<{ idClient: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { clientInfo } = useContext(AuthContext);
  const { customerOptions } = useContext(DataContext);

  // State management
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<CustomerYearSummaryRow[]>([]);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
  });

  // Background refresh state
  const [refreshLoading, setRefreshLoading] = useState<boolean>(false);
  const [refreshJobId, setRefreshJobId] = useState<string | null>(null);

  // Filter states
  const [customerId, setCustomerId] = useState<string>("");
  const [year, setYear] = useState<string>("");
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  >(null);
  const [sortBy, setSortBy] = useState<string>("year");
  const [sortDir, setSortDir] = useState<string>("desc");

  // Initialize filters from URL params
  useEffect(() => {
    const pageParam = searchParams.get("page");
    const pageSizeParam = searchParams.get("pageSize");
    const customerIdParam = searchParams.get("customerId");
    const yearParam = searchParams.get("year");
    const sortByParam = searchParams.get("sortBy");
    const sortDirParam = searchParams.get("sortDir");

    if (pageParam) {
      setPagination((prev) => ({ ...prev, current: parseInt(pageParam, 10) }));
    }
    if (pageSizeParam) {
      setPagination((prev) => ({
        ...prev,
        pageSize: parseInt(pageSizeParam, 10),
      }));
    }
    if (customerIdParam) {
      setCustomerId(customerIdParam);
    }
    if (yearParam) {
      setYear(yearParam);
    }
    if (sortByParam) {
      setSortBy(sortByParam);
    }
    if (sortDirParam) {
      setSortDir(sortDirParam);
    }
  }, [searchParams]);

  // Fetch data function
  const fetchData = async (params: any = {}) => {
    if (!idClient) return;

    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        idClient,
        page: (params.current || pagination.current || 1).toString(),
        pageSize: (params.pageSize || pagination.pageSize || 20).toString(),
        sortBy: params.sortBy || sortBy,
        sortDir: params.sortDir || sortDir,
        ...(customerId && { customerId }),
        ...(year && { year }),
        ...(dateRange &&
          dateRange[0] && { dateFrom: dateRange[0].format("YYYY-MM-DD") }),
        ...(dateRange &&
          dateRange[1] && { dateTo: dateRange[1].format("YYYY-MM-DD") }),
      });

      const response = await fetch(
        `${process.env.REACT_APP_DEV_API}/customer-year-summary?${queryParams}&qbo_origin=${process.env.REACT_APP_QBO_ORIGIN}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("info")}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: CustomerYearSummaryResponse = await response.json();

      if (result.success) {
        setData(result.rows);
        setPagination((prev) => ({
          ...prev,
          current: result.page,
          pageSize: result.pageSize,
          total: result.total,
        }));

        // Update URL params
        const newParams = new URLSearchParams(searchParams);
        newParams.set("page", result.page.toString());
        newParams.set("pageSize", result.pageSize.toString());
        if (customerId) newParams.set("customerId", customerId);
        else newParams.delete("customerId");
        if (year) newParams.set("year", year);
        else newParams.delete("year");
        newParams.set("sortBy", params.sortBy || sortBy);
        newParams.set("sortDir", params.sortDir || sortDir);
        setSearchParams(newParams);
      } else {
        message.error("Failed to fetch customer year summary");
      }
    } catch (error) {
      console.error("Error fetching customer year summary:", error);
      message.error("Failed to fetch data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    if (idClient) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idClient]);

  // Handle table changes (pagination, sorting)
  const handleTableChange = (
    newPagination: TablePaginationConfig,
    filters: any,
    sorter: any,
  ) => {
    const newSortBy = sorter.field || "year";
    const newSortDir = sorter.order === "ascend" ? "asc" : "desc";

    setSortBy(newSortBy);
    setSortDir(newSortDir);

    fetchData({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      sortBy: newSortBy,
      sortDir: newSortDir,
    });
  };

  // Handle filter changes
  const handleFilterChange = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData({ current: 1 });
  };

  // Export to CSV function
  const exportToCSV = () => {
    if (data.length === 0) {
      message.warning("No data to export");
      return;
    }

    const headers = [
      "Customer",
      "Year",
      "Invoices Count",
      "Invoices Total",
      "Credit Memos Count",
      "Credit Memos Total",
      "Payments Count",
      "Payments Total",
    ];

    const csvContent = [
      headers.join(","),
      ...data.map((row) =>
        [
          `"${row.customerName || "Unknown Customer"}"`,
          row.year,
          row.invoices_count,
          row.invoices_total,
          row.creditMemos_count,
          row.creditMemos_total,
          row.payments_count,
          row.payments_total,
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `customer-year-summary-${dayjs().format("YYYY-MM-DD")}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Reset filters
  const resetFilters = () => {
    setCustomerId("");
    setYear("");
    setDateRange(null);
    setPagination((prev) => ({ ...prev, current: 1 }));

    // Clear URL params
    const newParams = new URLSearchParams();
    newParams.set("page", "1");
    newParams.set("pageSize", pagination.pageSize?.toString() || "20");
    newParams.set("sortBy", "year");
    newParams.set("sortDir", "desc");
    setSearchParams(newParams);

    fetchData({ current: 1, sortBy: "year", sortDir: "desc" });
  };

  // Background refresh from QuickBooks
  const refreshFromQuickBooks = async () => {
    if (!idClient) {
      message.error("Client ID not found");
      return;
    }

    setRefreshLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_DEV_API}/reporting-data-refresh/${idClient}?qbo_origin=${process.env.REACT_APP_QBO_ORIGIN}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("info")}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data && result.data.jobId) {
        setRefreshJobId(result.data.jobId);
        message.success(
          "Background refresh started! Data will be updated in a few minutes.",
        );

        // Start monitoring the job progress
        monitorRefreshProgress(result.data.jobId);
      } else {
        message.error(result.message || "Failed to start background refresh");
      }
    } catch (error) {
      console.error("Error starting background refresh:", error);
      message.error("Failed to start background refresh. Please try again.");
    } finally {
      setRefreshLoading(false);
    }
  };

  // Monitor refresh progress
  const monitorRefreshProgress = (jobId: string) => {
    const interval = setInterval(async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_DEV_API}/reporting-job-status/${jobId}?qbo_origin=${process.env.REACT_APP_QBO_ORIGIN}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("info")}`,
            },
          },
        );

        if (response.ok) {
          const status = await response.json();

          if (status.status === "completed") {
            clearInterval(interval);
            setRefreshJobId(null);
            message.success(
              "QuickBooks data refresh completed! You can now refresh the page to see updated data.",
            );
          } else if (status.status === "failed") {
            clearInterval(interval);
            setRefreshJobId(null);
            message.error(
              "Background refresh failed. Please try again or contact support.",
            );
          }
        }
      } catch (error) {
        console.error("Error checking job status:", error);
      }
    }, 5000); // Check every 5 seconds

    // Clean up interval after 30 minutes
    setTimeout(
      () => {
        clearInterval(interval);
        if (refreshJobId) {
          setRefreshJobId(null);
          message.warning(
            "Background refresh monitoring stopped. Please refresh the page manually to check for updates.",
          );
        }
      },
      30 * 60 * 1000,
    );
  };

  return (
    <div className="general-view">
      <Row gutter={[24, 0]}>
        <ColumnComponent>
          <CardTitle
            title={`${clientInfo?.name || "Client"} - Customer Year Summary`}
          />
        </ColumnComponent>

        {/* Filters Card */}
        <ColumnComponent>
          <Card
            title="Filters & Actions"
            size="small"
            extra={
              <Space>
                <Tooltip title="Refresh ALL data from QuickBooks (background process)">
                  <Button
                    icon={<CloudSyncOutlined />}
                    onClick={refreshFromQuickBooks}
                    loading={refreshLoading}
                    disabled={!!refreshJobId}
                    type="primary"
                    ghost
                  >
                    {refreshJobId ? "Refreshing..." : "Refresh from QuickBooks"}
                  </Button>
                </Tooltip>
                <Tooltip title="Export to CSV">
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={exportToCSV}
                    disabled={data.length === 0}
                  >
                    Export
                  </Button>
                </Tooltip>
                <Tooltip title="Refresh Data">
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => fetchData()}
                    loading={loading}
                  >
                    Refresh
                  </Button>
                </Tooltip>
                <Tooltip title="Reset all filters">
                  <Button onClick={resetFilters}>Reset</Button>
                </Tooltip>
              </Space>
            }
          >
            <Space wrap size="middle">
              <div>
                <Text strong>Customer:</Text>
                <br />
                <Select
                  placeholder="All Customers"
                  style={{ width: 200 }}
                  value={customerId || undefined}
                  onChange={(value) => {
                    setCustomerId(value || "");
                  }}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {customerOptions?.map((customer: any) => (
                    <Option key={customer.value} value={customer.value}>
                      {customer.label}
                    </Option>
                  ))}
                </Select>
              </div>

              <div>
                <Text strong>Year:</Text>
                <br />
                <Select
                  placeholder="All Years"
                  style={{ width: 120 }}
                  value={year || undefined}
                  onChange={(value) => {
                    setYear(value || "");
                  }}
                  allowClear
                >
                  {Array.from({ length: 10 }, (_, i) => {
                    const currentYear = new Date().getFullYear();
                    const yearValue = currentYear - i;
                    return (
                      <Option key={yearValue} value={yearValue.toString()}>
                        {yearValue}
                      </Option>
                    );
                  })}
                </Select>
              </div>

              <div>
                <Text strong>Date Range:</Text>
                <br />
                <RangePicker
                  value={dateRange}
                  onChange={(dates) => {
                    setDateRange(dates);
                  }}
                  format="YYYY-MM-DD"
                />
              </div>

              <div>
                <br />
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleFilterChange}
                  loading={loading}
                >
                  Apply Filters
                </Button>
              </div>
            </Space>
          </Card>
        </ColumnComponent>

        {/* Data Table */}
        <ColumnComponent>
          {loading && data.length === 0 ? (
            <div style={{ minHeight: "400px" }}>
              <Flex
                gap="middle"
                align="center"
                vertical
                justify="center"
                style={{ height: "400px" }}
              >
                <Spin size="large" />
                <Text>Loading customer year summary...</Text>
              </Flex>
            </div>
          ) : (
            <Card>
              <Table
                columns={getColumns()}
                dataSource={data}
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                rowKey={(record) => `${record.customerId}-${record.year}`}
                bordered
                size="small"
                scroll={{ x: "100%" }}
                className="customer-year-summary-table"
                summary={() => {
                  if (data.length === 0) return null;

                  const totals = data.reduce(
                    (acc, row) => ({
                      invoices_count: acc.invoices_count + row.invoices_count,
                      invoices_total: acc.invoices_total + row.invoices_total,
                      creditMemos_count:
                        acc.creditMemos_count + row.creditMemos_count,
                      creditMemos_total:
                        acc.creditMemos_total + row.creditMemos_total,
                      payments_count: acc.payments_count + row.payments_count,
                      payments_total: acc.payments_total + row.payments_total,
                    }),
                    {
                      invoices_count: 0,
                      invoices_total: 0,
                      creditMemos_count: 0,
                      creditMemos_total: 0,
                      payments_count: 0,
                      payments_total: 0,
                    },
                  );

                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0}>
                          <Text strong>Page Total</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} />
                        <Table.Summary.Cell index={2} align="right">
                          <Text strong>
                            {totals.invoices_count.toLocaleString()}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="right">
                          <Text strong>
                            {USDollar.format(totals.invoices_total)}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="right">
                          <Text strong>
                            {totals.creditMemos_count.toLocaleString()}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5} align="right">
                          <Text strong>
                            {USDollar.format(totals.creditMemos_total)}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={6} align="right">
                          <Text strong>
                            {totals.payments_count.toLocaleString()}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={7} align="right">
                          <Text strong>
                            {USDollar.format(totals.payments_total)}
                          </Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            </Card>
          )}
        </ColumnComponent>

        {/* Help Button */}
        <ColumnComponent>
          <div style={{ textAlign: "right" }}>
            <Tooltip title="This report shows transaction counts and totals grouped by customer and year. Use filters to narrow down results and export data for further analysis.">
              <Button
                icon={<QuestionCircleOutlined style={{ color: "#17549a" }} />}
                type="default"
              >
                Help
              </Button>
            </Tooltip>
          </div>
        </ColumnComponent>
      </Row>
    </div>
  );
};

export default CustomerYearSummaryPage;
